import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useChartAccessibility } from '../../hooks/useChartAccessibility';
import { AccessibleDataTable } from '../accessibility/AccessibleDataTable';

export interface LineChartDataPoint {
  name: string;
  [key: string]: string | number;
}

export interface LineConfig {
  dataKey: string;
  name?: string;
  color?: string;
  strokeWidth?: number;
  dot?: boolean;
  type?: 'linear' | 'monotone' | 'step';
}

export interface LineChartProps {
  data: LineChartDataPoint[];
  lines: LineConfig[];
  height?: number | string;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  xAxisDataKey?: string;
  yAxisLabel?: string;
  className?: string;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
];

export function LineChart({
  data,
  lines,
  height = '100%',
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  xAxisDataKey = 'name',
  yAxisLabel,
  className = '',
}: LineChartProps) {
  const a11y = useChartAccessibility();
  const colorAt = (index: number) =>
    a11y.resolveColor(index, lines[index]?.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]);

  const tableColumns = [
    { key: xAxisDataKey, label: 'Name' },
    ...lines.map((l) => ({ key: l.dataKey, label: l.name || l.dataKey })),
  ];

  return (
    <div className={`w-full h-full flex flex-col ${className}`} style={typeof height === 'number' ? { height } : undefined}>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsLineChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={a11y.gridColor} />}
            <XAxis
              dataKey={xAxisDataKey}
              tick={{ fontSize: 12 }}
              stroke={a11y.axisColor}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke={a11y.axisColor}
              label={
                yAxisLabel
                  ? { value: yAxisLabel, angle: -90, position: 'insideLeft' }
                  : undefined
              }
            />
            {showTooltip && (
              <Tooltip
                contentStyle={{
                  backgroundColor: a11y.tooltipBg,
                  border: `1px solid ${a11y.tooltipBorder}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  color: a11y.tooltipText,
                }}
                labelStyle={{ color: a11y.tooltipText }}
                itemStyle={{ color: a11y.tooltipText }}
              />
            )}
            {showLegend && <Legend />}
            {lines.map((line, index) => (
              <Line
                key={line.dataKey}
                type={line.type || 'monotone'}
                dataKey={line.dataKey}
                name={line.name || line.dataKey}
                stroke={colorAt(index)}
                strokeWidth={line.strokeWidth || 2}
                dot={line.dot !== false}
                activeDot={{ r: 6 }}
                isAnimationActive={a11y.isAnimationActive}
                animationDuration={a11y.animationDuration}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
      <AccessibleDataTable caption="Line chart data" columns={tableColumns} data={data} />
    </div>
  );
}
